import React, { Suspense, useMemo } from "react";
import Flx from "../../../../components/layout/Flx";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { Box, Card, CircularProgress } from "@mui/material";
import SubsectionHeader from "../../../../components/ui/SubsectionHeader";
import size from "lodash/size";
import {
  AlarmOutlined,
  CancelRounded,
  CheckCircleOutline,
  CheckOutlined,
  CheckRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import Txt from "../../../../components/typography/Txt";
import LoadingFallback from "../../../../components/LoadingFallback";
import { green, grey, orange, red } from "@mui/material/colors";
import OverviewItem from "../../../../components/data/OverviewItem";

/**
 * Validity configuration.
 * Your original matchedDocTypesWithValidity list only had comments for days.
 * Here we make a best-effort machine-readable map.
 *
 * If you want to change days later, just edit DOC_VALIDITY_DAYS.
 * Use value "YEAR_END" for docs that expire at year end.
 */
const DOC_VALIDITY_DAYS = {
  // Appraisal
  "Secondary Valuation - CDA/ARR": 120,
  "Secondary Valuation - Internal": 120,

  // Borrower Documents
  "Background Report": 90,
  "Credit Report": 90,
  "Loan Application": 180,
  "Borrower Experience - Property Reports": 90,

  // Closing Package
  "Closing Protection Letter": 90,
  "Marked Up Commitment": 90,

  // Liquidity
  "Ocrolus Verification": 45,

  // Property Documents
  "Flood Certificate": "YEAR_END",

  // Terms & Sizer
  "Inspection Report & supporting Draws and Wires": 180,
  "Preliminary HUD": 90,
  "Settlement Statement": 90,

  // Third Party Reports
  "Project Feasibility Analysis": 180,
  "Environmental Report - Phase 1": 180,
  "Environmental Report - Screen Assessment (ESA)": 180,

  // Title Docs
  "Title - CPL": 90,
  "Title - Wire Instructions": 90,
  "Funding Shield Verification - Wire Instructions": 90,
};

/**
 * Original groups/types list (kept for matching by group name).
 * If reportGuidelineExpirationDays missing and docType doesn't match a key above,
 * we'll try matching by group name and fall back to a group default if provided.
 *
 * Right now group defaults are not specified in your data, so group match is
 * only used to allow docType matching within that group list.
 */
export const matchedDocTypesWithValidity = {
  Appraisal: [
    "Secondary Valuation - CDA/ARR",
    "Secondary Valuation - Internal",
  ],
  "Borrower Documents": [
    "Background Report",
    "Credit Report",
    "Loan Application",
    "Borrower Experience - Property Reports",
  ],
  "Closing Package": ["Closing Protection Letter", "Marked Up Commitment"],
  Liquidity: ["Ocrolus Verification"],
  "Property Documents": ["Flood Certificate"],
  "Terms & Sizer": [
    "Inspection Report & supporting Draws and Wires",
    "Preliminary HUD",
    "Settlement Statement",
  ],
  "Third Party Reports": [
    "Project Feasibility Analysis",
    "Environmental Report - Phase 1",
    "Environmental Report - Screen Assessment (ESA)",
  ],
  "Title Docs": [
    "Title - CPL",
    "Title - Wire Instructions",
    "Funding Shield Verification - Wire Instructions",
  ],
};

const EXPIRING_SOON_THRESHOLD_DAYS = 10;
const MAX_RESULTS_GRID_WIDTH = "420px";

const LoanExpiringDocumentsRenderer = ({ documents = [] }) => {
  const reportDocs = useMemo(
    () => documents.filter((doc) => doc.reportEffectiveDate && !doc?.isHidden),
    [documents]
  );

  const docsWithStatus = useMemo(() => {
    const nowMs = Date.now();

    return reportDocs.map((doc) => {
      const effective = new Date(doc.reportEffectiveDate);
      const effectiveMs = effective.getTime();

      const daysToExpire = getExpirationDays(doc);
      const expiration = computeExpirationDate(effective, daysToExpire);

      const expirationMs = expiration?.getTime?.() ?? effectiveMs;

      const totalSpan = Math.max(1, expirationMs - effectiveMs);
      const remainingSpan = expirationMs - nowMs;

      const percentRemaining = Math.max(
        0,
        Math.min(100, (remainingSpan / totalSpan) * 100)
      );

      const remainingDays = Math.ceil(remainingSpan / (1000 * 60 * 60 * 24));

      let status;
      if (remainingDays < 0) status = "expired";
      else if (remainingDays <= EXPIRING_SOON_THRESHOLD_DAYS)
        status = "expiringSoon";
      else status = "valid";

      return {
        ...doc,
        effective,
        expirationDays: daysToExpire,
        expiration,
        remainingDays,
        status,
        percentRemaining,
      };
    });
  }, [reportDocs]);

  if (size(reportDocs) === 0) {
    return null;
  }
  return (
    <Flx column sx={{ mt: 1, mb: 3, p: 2 }}>
      <SubsectionHeader
        title={`Expiring Documents (${size(reportDocs)})`}
        // title={`Time Sensitive Documents (${size(reportDocs)})`}
        icon={<AlarmOutlined className="thin" fontSize="small" />}
      />

      <FlxGrid gap={2} width={MAX_RESULTS_GRID_WIDTH} grow={0}>
        {docsWithStatus.map((doc) => (
          <DocumentCard
            key={doc._id || doc.file_id || doc.loanNumber}
            doc={doc}
          />
        ))}
      </FlxGrid>
    </Flx>
  );
};

/** -------- helpers -------- */

const normalize = (v) =>
  String(v || "")
    .toLowerCase()
    .trim();

const findDocTypeValidityDays = (docType) => {
  if (!docType) return null;
  if (DOC_VALIDITY_DAYS[docType] != null) return DOC_VALIDITY_DAYS[docType];

  // try fuzzy "contains" match against known keys
  const docTypeNorm = normalize(docType);
  const matchKey = Object.keys(DOC_VALIDITY_DAYS).find(
    (k) =>
      normalize(k).includes(docTypeNorm) || docTypeNorm.includes(normalize(k))
  );
  if (matchKey) return DOC_VALIDITY_DAYS[matchKey];

  return null;
};

const findGroupValidityDays = (docGroup) => {
  if (!docGroup) return null;
  const groupNorm = normalize(docGroup);

  // If group name matches one of the keys, we still need a doc type to select days.
  // Since your source doesn't include group-level default days, return null here.
  const groupKey = Object.keys(matchedDocTypesWithValidity).find(
    (k) => normalize(k) === groupNorm
  );
  if (!groupKey) return null;

  return null;
};

const getExpirationDays = (doc) => {
  const explicitDays = Number(doc.reportGuidelineExpirationDays);
  if (!Number.isNaN(explicitDays) && explicitDays > 0) return explicitDays;

  const byDocType =
    findDocTypeValidityDays(doc.docType) ||
    findDocTypeValidityDays(doc.file_display_name);
  if (byDocType != null) return byDocType;

  const byGroup =
    findGroupValidityDays(doc.docGroup) ||
    findGroupValidityDays(doc.group) ||
    findGroupValidityDays(doc.docTypeGroup);
  if (byGroup != null) return byGroup;

  // no match => unknown validity
  return null;
};

const computeExpirationDate = (effectiveDate, expirationDays) => {
  if (!effectiveDate || Number.isNaN(effectiveDate.getTime())) return null;

  if (expirationDays === "YEAR_END") {
    const y = effectiveDate.getFullYear();
    const yearEnd = new Date(y, 11, 31, 23, 59, 59, 999);
    return yearEnd;
  }

  const daysNum = Number(expirationDays);
  if (Number.isNaN(daysNum) || daysNum <= 0) return null;

  return new Date(effectiveDate.getTime() + daysNum * 24 * 60 * 60 * 1000);
};

/** -------- UI -------- */

const DocumentCard = ({ doc }) => {
  const { chipColor, chipLabel, chipIcon, chipBackgroundColor } = getChipStyles(
    doc.status,
    doc.remainingDays
  );

  const expDaysLabel =
    doc.expirationDays == null
      ? "Unknown"
      : doc.expirationDays === "YEAR_END"
        ? "Year End"
        : `${doc.expirationDays} days`;

  const expirationDateLabel = doc.expiration
    ? doc.expiration.toLocaleDateString()
    : "Unknown";
  return (
    <Card
      key={doc._id}
      sx={{
        flexBasis: "340px",
        flexGrow: 1,
        flexShrink: 1,
        p: 1.5,
      }}
    >
      <Flx column gap={1.2}>
        {/* header */}
        <Flx
          fw
          jb
          ac
          sx={{
            gap: 0.8,
            // display: "grid",
            // gridTemplateColumns: "40px 1fr",
            // alignItems: "center",
          }}
        >
          <Flx column gap={0.2}>
            <Txt
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "12px",
                color: grey[900],
              }}
            >
              {doc?.docType}
            </Txt>
            <Txt sx={{ fontSize: "10px" }} secondary fontStyle="italic">
              {doc?.file_display_name}
            </Txt>
          </Flx>
          <Flx
            column
            center
            sx={{
              height: "36px",
              px: 1,
              // width: "36px",
              // background: green[100],
              background: chipBackgroundColor,
              borderRadius: 1,
            }}
          >
            {chipIcon}
            <Txt sx={{ fontSize: "9px", color: chipColor }}>{chipLabel}</Txt>
          </Flx>
        </Flx>

        {/* dates + status */}
        <Flx
          gap={3}
          sx={{
            mt: 1,
            justifyContent: "space-between",
            ".txt": { fontSize: "11px" },
          }}
        >
          <OverviewItem
            label={`Effective Date:`}
            value={
              doc.effective ? doc.effective.toLocaleDateString() : "Unknown"
            }
          />
          <OverviewItem
            label={`Expiration Date:`}
            value={expirationDateLabel}
          />
          <OverviewItem label={`Valid For:`} value={expDaysLabel} />
          <OverviewItem
            label={`Days Remaining:`}
            value={
              doc.remainingDays == null
                ? "Unknown"
                : `${doc.remainingDays} day(s)`
            }
          />
        </Flx>
        {/* <Flx jb ac>
          <Flx gap={3} sx={{ ".txt": { fontSize: "11px" } }}>
            <OverviewItem
              label={`Start:`}
              value={
                doc.effective ? doc.effective.toLocaleDateString() : "Unknown"
              }
            />
            <OverviewItem label={`Valid For:`} value={expDaysLabel} />
            <OverviewItem
              label={`Remaining:`}
              value={
                doc.remainingDays == null
                  ? "Unknown"
                  : `${doc.remainingDays} day(s)`
              }
            />
          </Flx>

          <Flx ac gap={0.6}>
      
            <Flx
              ac
              sx={{
                background: chipBackgroundColor,
                py: 0.35,
                pr: 0.8,
                pl: 0.6,
                borderRadius: 1,
              }}
            >
              {chipIcon}
              <Txt
                sx={{
                  textWrap: "nowrap",
                  color: chipColor,
                  lineHeight: 1.2,
                  fontSize: "11px",
                  fontWeight: "bold",
                }}
              >
                {chipLabel}
              </Txt>
            </Flx>

          
            {doc.remainingDays != null && doc.remainingDays > 0 && (
              <Flx column sx={{ position: "relative" }}>
                <CircularProgress
                  variant="determinate"
                  value={doc.percentRemaining}
                  thickness={5}
                  sx={{
                    width: "46px !important",
                    height: "46px !important",
                    color: chipColor,
                    backgroundColor: "#f5f5f5",
                    borderRadius: "50%",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "23px",
                    left: "23px",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    background: "#ffffff",
                    justifyContent: "center",
                    borderRadius: "50%",
                    flexDirection: "column",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <Txt
                    sx={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: chipColor,
                      lineHeight: 1,
                    }}
                  >
                    {doc.remainingDays}
                  </Txt>
                  <Txt
                    sx={{
                      fontSize: "8px",
                      lineHeight: 1,
                      color: chipColor,
                    }}
                  >
                    Days
                  </Txt>
                </Box>
              </Flx>
            )}
          </Flx>
        </Flx> */}
      </Flx>
    </Card>
  );
};

const getChipStyles = (status, remainingDays) => {
  let chipColor, chipLabel, chipIcon, chipBackgroundColor;

  switch (status) {
    case "valid":
      chipColor = green[700];
      chipBackgroundColor = green[50];
      chipLabel = "Valid";
      chipIcon = <CheckOutlined sx={{ color: green[700], fontSize: "15px" }} />;
      break;

    case "expiringSoon":
      chipColor = orange[700];
      chipBackgroundColor = orange[50];
      chipLabel = remainingDays === 0 ? "Expires Today" : "Expiring";
      chipIcon = (
        <WarningAmberRounded sx={{ color: orange[700], fontSize: "15px" }} />
      );
      break;

    case "expired":
      chipColor = red[700];
      chipBackgroundColor = red[50];
      chipLabel = "Expired";
      chipIcon = <CancelRounded sx={{ color: red[700], fontSize: "15px" }} />;
      break;

    default:
      chipColor = grey[600];
      chipBackgroundColor = grey[100];
      chipLabel = "Unknown";
      chipIcon = (
        <WarningAmberRounded sx={{ color: grey[600], fontSize: "15px" }} />
      );
      break;
  }

  return { chipColor, chipLabel, chipIcon, chipBackgroundColor };
};

export default LoanExpiringDocumentsRenderer;
